const TABLE_LEFT = 12;
const TABLE_WIDTH = 186;
const A4_WIDTH = 210;
const A4_HEIGHT = 297;
const BRAND_PLUM = [46, 26, 70];
const BODY_TEXT = [31, 41, 55];

const IMAGE_EXTENSIONS = new Set([".png", ".jpg", ".jpeg", ".webp", ".gif", ".bmp"]);

const fileHostFromEnv = () => {
  const raw = (
    typeof process !== "undefined" &&
    process.env.REACT_APP_API_URL
  ) || "http://localhost:5000/api";
  const withoutApi = raw.replace(/\/api\/?$/, "");
  if (/^https?:\/\//i.test(withoutApi)) return withoutApi;
  if (withoutApi.startsWith("//")) return `${window.location.protocol}${withoutApi}`;
  if (withoutApi.startsWith("/")) return "http://localhost:5000";
  return `http://${withoutApi}`;
};

const FILE_HOST = fileHostFromEnv();

const addFullPageImage = (doc, image) => {
  doc.addImage(image, "JPEG", 0, 0, A4_WIDTH, A4_HEIGHT);
};

const stripHtml = (value) => String(value || "").replace(/<[^>]*>/g, "").trim();

export const resolveQuoteFileUrl = (file) => {
  const path = typeof file === "string" ? file : file?.url || file?.path || "";
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${FILE_HOST}${path.startsWith("/") ? path : `/${path}`}`;
};

export const getQuoteFileEntries = (quote) => {
  const files = Array.isArray(quote?.files) ? quote.files : [];
  return files
    .map((file) => {
      const path = typeof file === "string" ? file : file?.url || file?.path || "";
      const name = (
        typeof file === "string"
          ? path.split("/").pop()
          : file?.name || path.split("/").pop() || "file"
      );
      const ext = (name.includes(".") ? name.slice(name.lastIndexOf(".")) : "").toLowerCase();
      const url = resolveQuoteFileUrl(path);
      return {
        path,
        name,
        ext,
        url,
        isImage: IMAGE_EXTENSIONS.has(ext),
        isPdf: ext === ".pdf",
      };
    })
    .filter((entry) => entry.url);
};

const fetchWithAuth = async (url) => {
  const token = typeof localStorage !== "undefined" ? localStorage.getItem("ts_token") : null;
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`Could not load file (${res.status})`);
  return res;
};

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const getImageDimensions = (dataUrl) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.width, height: img.height });
    img.onerror = () => reject(new Error("Could not read image dimensions"));
    img.src = dataUrl;
  });

const imageFormatFromDataUrl = (dataUrl) => (String(dataUrl).startsWith("data:image/png") ? "PNG" : "JPEG");

const fitImageSizeMm = (imgW, imgH, maxW, maxH) => {
  if (!imgW || !imgH) return { width: maxW, height: maxH * 0.5 };
  let width = maxW;
  let height = (imgH / imgW) * width;
  if (height > maxH) {
    height = maxH;
    width = (imgW / imgH) * height;
  }
  return { width, height };
};

export const loadQuoteAttachmentPdfBytes = async (quote) => {
  const pdfEntries = getQuoteFileEntries(quote).filter((entry) => entry.isPdf);
  const buffers = [];

  for (const entry of pdfEntries) {
    try {
      const res = await fetchWithAuth(entry.url);
      buffers.push(await res.arrayBuffer());
    } catch (error) {
      console.warn("Skipped quote PDF attachment:", entry.name, error);
    }
  }

  return buffers;
};

export const addRemarksAndFilesSection = async (doc, bgPage, previousY, quote) => {
  const remarks = stripHtml(quote?.remarks);
  const imageEntries = getQuoteFileEntries(quote).filter((entry) => entry.isImage);
  const hasContent = remarks.length > 0 || imageEntries.length > 0;

  if (!hasContent) return previousY;

  const bottomMargin = 20;
  let currentY = previousY + 12;

  const ensureSpace = (neededHeight) => {
    if (currentY + neededHeight > A4_HEIGHT - bottomMargin) {
      doc.addPage();
      addFullPageImage(doc, bgPage);
      currentY = 42;
    }
  };

  if (remarks.length > 0) {
    const maxWidth = TABLE_WIDTH;
    const lineHeight = 4;

    doc.setFont("Montserrat", "bold");
    doc.setFontSize(10);
    doc.setTextColor(...BRAND_PLUM);

    ensureSpace(10);
    doc.text("Remarks", TABLE_LEFT, currentY);
    currentY += 6;

    doc.setFont("Montserrat", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...BODY_TEXT);

    const lines = doc.splitTextToSize(remarks, maxWidth);
    lines.forEach((line) => {
      ensureSpace(lineHeight + 1);
      doc.text(line, TABLE_LEFT, currentY);
      currentY += lineHeight;
    });

    currentY += 6;
  }

  for (const entry of imageEntries) {
    try {
      const res = await fetchWithAuth(entry.url);
      const blob = await res.blob();
      const dataUrl = await blobToDataUrl(blob);
      const format = imageFormatFromDataUrl(dataUrl);
      const { width: imgW, height: imgH } = await getImageDimensions(dataUrl);
      const maxHeight = A4_HEIGHT - bottomMargin - currentY - 8;
      const { width: drawW, height: drawH } = fitImageSizeMm(imgW, imgH, TABLE_WIDTH, Math.max(40, maxHeight));

      ensureSpace(drawH + 8);
      doc.addImage(dataUrl, format, TABLE_LEFT, currentY, drawW, drawH);
      currentY += drawH + 8;
    } catch (error) {
      console.warn("Skipped quote image attachment:", entry.name, error);
      doc.setFont("Montserrat", "normal");
      doc.setFontSize(8);
      doc.setTextColor(...BODY_TEXT);
      ensureSpace(6);
      doc.text(`Could not load attachment: ${entry.name}`, TABLE_LEFT, currentY);
      currentY += 6;
    }
  }

  return currentY;
};
